import { useState, useEffect } from 'react'
import { useDashboardStore } from '../../store/dashboardStore'
import { useT } from '../../hooks/useTranslation'
import { getStats } from '../../services/api'
import Leaderboard from './Leaderboard'

function CircularProgress({ pct, size = 40 }) {
  const r = (size - 6) / 2
  const circ = 2 * Math.PI * r
  const filled = (pct / 100) * circ
  const color = pct >= 50 ? '#34D399' : '#FF3B30'

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={3}
          strokeDasharray={`${filled.toFixed(1)} ${circ.toFixed(1)}`}
          strokeLinecap="round"
        />
      </svg>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 700, color,
      }}>
        {pct}%
      </div>
    </div>
  )
}

function AssetRow({ rank, asset, isLast, tradesLabel }) {
  const isPos = asset.pnl >= 0
  const pnlColor = isPos ? '#34D399' : '#FF3B30'
  const pnlText = `${isPos ? '+' : ''}$${Math.abs(asset.pnl).toFixed(2)}`

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      paddingTop: 9, paddingBottom: 9,
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{ fontSize: 12, color: '#636366', width: 14, textAlign: 'center', flexShrink: 0 }}>
        {rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#FFF', lineHeight: 1.2 }}>{asset.pair}</div>
        <div style={{ fontSize: 10, color: '#636366', marginTop: 2 }}>{asset.trades} {tradesLabel}</div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: pnlColor, flexShrink: 0 }}>
        {pnlText}
      </span>
      <CircularProgress pct={asset.winRate} size={40} />
    </div>
  )
}

export default function KpiStats() {
  const t = useT()
  const period = useDashboardStore(s => s.period)
  const [d, setD] = useState({ winRate: 0, totalTrades: 0, avgProfit: 0 })
  const assets = []

  useEffect(() => {
    let cancelled = false
    const load = () => getStats(period)
      .then(stats => {
        if (cancelled) return
        setD({
          winRate:     stats.winRate     ?? 0,
          totalTrades: stats.totalTrades ?? 0,
          avgProfit:   stats.avgProfit   ?? 0,
        })
      })
      .catch(() => {})
    load()
    const id = setInterval(load, 15000)
    return () => { cancelled = true; clearInterval(id) }
  }, [period])

  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#8E8E93', marginBottom: 10 }}>
        {t.tradingStats}
      </h3>

      {/* Total Trades */}
      <div style={{ background: '#1A1A1A', borderRadius: 14, padding: '12px 14px', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#636366', fontWeight: 500, display: 'block', marginBottom: 4 }}>
          {t.kpiTotalTrades}
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.2 }}>
          {d.totalTrades}
        </span>
      </div>

      {/* Leaderboard */}
      <Leaderboard />
    </div>
  )
}
